import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCampaignGenerationJob,
  listCampaignGenerationJobs,
} from 'services/acg/phase-1';
import { SOCKET_EVENTS, SOCKET_NAME, useSocket } from 'socket';
import { useEffect } from 'react';
import type { CampaignGenerationJob } from 'containers/AutomatedCampaignGeneration/Phase1/CampaignGeneration/types';
import { User } from 'containers/AutomatedCampaignGeneration/Phase1/CampaignGeneration/types';
import _ from 'lodash';
import objectPath from 'object-path';

export const useGetCampaignGenerationJobs = (page, limit) => {
  return useQuery(['getCampaignGenerationJobs', { page, limit }], () =>
    listCampaignGenerationJobs(page, limit),
  );
};

export const useGetCampaignGenerationJobDetails = (id: string) => {
  return useQuery(['getCampaignGenerationJob', id], () =>
    getCampaignGenerationJob(id),
  );
};

export const useCampaignGenerationJobSubscription = () => {
  const queryClient = useQueryClient();

  function onCampaignGenerationJobCreated(data: {
    campaignGenerationJob: CampaignGenerationJob,
    user: User,
  }) {
    queryClient.setQueriesData(
      {
        queryKey: ['getCampaignGenerationJobs', { page: 1 }],
        type: 'active',
      },
      (oldData: {
        data: CampaignGenerationJob[],
        users: { [key: string]: User },
        total: number,
      }) => {
        if (!oldData) return oldData;
        const cloned = _.cloneDeep(oldData);
        objectPath.set(
          cloned,
          `data`,
          _.take([data.campaignGenerationJob, ...cloned.data], 10),
        );
        objectPath.set(cloned, `users.${data.user.id}`, data.user.id);
        objectPath.set(cloned, 'total', cloned.total + 1);
        return cloned;
      },
    );
    void queryClient.invalidateQueries({
      queryKey: ['getCampaignGenerationJobs'],
      type: 'inactive',
    });
  }

  function onCampaignGenerationJobUpdated(data: {
    campaignGenerationJob: CampaignGenerationJob,
    user: User,
  }) {
    queryClient.setQueryData(
      ['getCampaignGenerationJob', data.campaignGenerationJob.id],
      () => {
        return data;
      },
    );
    queryClient.setQueriesData(
      {
        queryKey: ['getCampaignGenerationJobs'],
        type: 'active',
      },
      (oldData: {
        data: CampaignGenerationJob[],
        users: { [key: string]: User },
        total: number,
      }) => {
        if (!oldData) return oldData;
        const cloned = _.cloneDeep(oldData);
        const jobIndex = cloned.data.findIndex(
          job => job.id === data.campaignGenerationJob.id,
        );
        objectPath.set(cloned, `data.${jobIndex}`, data.campaignGenerationJob);
        objectPath.set(cloned, `users.${data.user.id}`, data.user);
        return cloned;
      },
    );
  }
  const socket = useSocket(SOCKET_NAME.ACG_PHASE_1);
  useEffect(() => {
    socket.on(
      SOCKET_EVENTS.ACG_PHASE_1.CAMPAIGN_GENERATION_JOB_CREATED,
      onCampaignGenerationJobCreated,
    );
    socket.on(
      SOCKET_EVENTS.ACG_PHASE_1.CAMPAIGN_GENERATION_JOB_UPDATED,
      onCampaignGenerationJobUpdated,
    );
    return () => {
      socket.off(
        SOCKET_EVENTS.ACG_PHASE_1.CAMPAIGN_GENERATION_JOB_CREATED,
        onCampaignGenerationJobCreated,
      );
      socket.off(
        SOCKET_EVENTS.ACG_PHASE_1.CAMPAIGN_GENERATION_JOB_UPDATED,
        onCampaignGenerationJobUpdated,
      );
    };
  }, [socket]);
};
